import React from 'react';
import { useTranslation } from 'react-i18next';
import { BotSourceProviderConfig } from '../../../shared/constants/botSources';
import { DataQualityPopover } from './data-quality/DataQualityPopover';
import { DataQualityState } from './data-quality/dataQualityHelpers';

interface SourceTileProps {
  config: BotSourceProviderConfig;
  isActive: boolean;
  onConnect: () => void;
  onDisconnect?: () => void;
  onViewAnalysis?: () => void;
  disconnectPending?: boolean;
  disabled: boolean;
  /**
   * Optional i18n key overriding the default disconnect label/pending label.
   * Used by the CSV tile to render "Clear CSV data" / "Clearing..." instead of
   * "Disconnect" / "Disconnecting...".
   */
  disconnectLabelKey?: string;
  disconnectPendingLabelKey?: string;
  /**
   * Optional inline hint shown under the title when the tile is disabled
   * because another source is currently active for this project.
   */
  lockedHintKey?: string;
  /**
   * Optional source quality marker. When provided and the tile is active,
   * the connected source's Bot-Management capability is shown next to the
   * green "Connected" tag so the user knows the precision class of the
   * numbers they will see on the dashboard. Tooltip on the embedded info
   * icon explains the implication.
   */
  dataQuality?: DataQualityState;
}

export const SourceTile: React.FC<SourceTileProps> = ({
  config,
  isActive,
  onConnect,
  onDisconnect,
  onViewAnalysis,
  disconnectPending,
  disabled,
  disconnectLabelKey,
  disconnectPendingLabelKey,
  lockedHintKey,
  dataQuality,
}) => {
  const { t } = useTranslation();
  const title = t(config.titleKey);
  const description = t(config.descriptionKey);
  const disconnectLabel = disconnectPending
    ? t(disconnectPendingLabelKey ?? 'cloudflare.disconnecting')
    : t(disconnectLabelKey ?? 'disconnect');

  return (
    <div
      className={[
        'source-row',
        disabled ? 'source-row--disabled' : '',
        isActive ? 'source-row--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={`source-row__icon source-row__icon--${config.type}`}>
        {config.logoPath ? (
          <img src={config.logoPath} alt="" className="source-row__logo" aria-hidden="true" />
        ) : (
          <span className={`${config.iconClass} ico--md`} aria-hidden="true" />
        )}
      </div>
      <div className="source-row__text">
        <div className="source-row__title">
          {title}
          {isActive && (
            <span className="tag tag--stats tag--badge-success source-row__status">
              <span className="tag__stripe" />
              <span className="tag__value">{t('connected')}</span>
            </span>
          )}
          {isActive && dataQuality && (
            <DataQualityPopover state={dataQuality} size="chip" />
          )}
        </div>
        <div className="source-row__subtitle">{description}</div>
        {lockedHintKey && !isActive && (
          <div className="source-row__subtitle source-row__locked-hint">{t(lockedHintKey)}</div>
        )}
      </div>

      <div className="source-row__action">
        {isActive ? (
          <>
            {onViewAnalysis && (
              <button
                type="button"
                className="btn btn--sm"
                onClick={onViewAnalysis}
                aria-label={t('botsActivity.connect.viewAnalysisAria')}
              >
                {t('botsActivity.connect.viewAnalysis')}
                <span className="ico--right ico--xs" aria-hidden="true" />
              </button>
            )}
            {onDisconnect && (
              <button
                className="btn btn--sm btn--secondary"
                onClick={onDisconnect}
                disabled={disconnectPending}
                type="button"
              >
                {disconnectLabel}
              </button>
            )}
          </>
        ) : (
          (() => {
            const isUpload = config.type === 'log';
            const label = isUpload
              ? t('botsActivity.connect.uploadFile')
              : t('connect');
            const iconClass = isUpload ? 'ico--clipboard' : 'ico--add';
            return (
              <button
                className="btn btn--primary btn--sm source-row__connect"
                onClick={onConnect}
                disabled={disabled}
                aria-label={`${label} ${title}`}
                type="button"
              >
                <span className={`${iconClass} ico--xs`} aria-hidden="true" />
                <span className="source-row__connect-label">{label}</span>
              </button>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default SourceTile;
